import moment from "moment";
import GridDX from "../../../components/layout/griddx";
import PageTitle from "../../../components/layout/pagetitle";
import UnitsRangePicker from "../../../components/units_controls/units_rangepicker";
import { useTenantContext } from "../../../context/tenantcontext";
import {
  API_DATE_FORMAT,
  DATE_FORMAT,
  DateSorter,
  DATETIME_FORMAT,
  inboundRequestStatusColor,
} from "../../../shared/globals";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useReportService from "../../../shared/services/reportservice";
import { useNotificationContext } from "../../../context/notificationcontext";
import ListPageDX from "../../../components/business/listpagedx";
import { GridColDef } from "@mui/x-data-grid";
import TypeTranslator from "../../../shared/typetranslator";
import { useAuthContext } from "../../../context/authcontext";

const InboundReport = () => {
  const { tenantId } = useTenantContext();
  const { userData } = useAuthContext();
  const tenantIdFromUser =
    userData.userType === "T" ? userData.tenantId : tenantId;
  const { setError } = useNotificationContext();
  const { getInboundReportForTenant } = useReportService();
  const {
    inboundRequestStatuses,
    getInboundDeliveryTypeValue,
    getInboundRequestStatusValue,
  } = TypeTranslator();
  const { i18n, t } = useTranslation();
  const [startDate, setStartDate] = useState<string | null>(
    moment().format(API_DATE_FORMAT),
  );
  const [endDate, setEndDate] = useState<string | null>(
    moment().format(API_DATE_FORMAT),
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<any[]>([]);
  const columns: GridColDef[] = [
    {
      field: "inboundRequestId",
      headerName: `${t("Id")}`,
      flex: 1,
      maxWidth: 50,
    },
    {
      field: "requestDate",
      headerName: `${t("Request Date")}`,
      sortComparator: DateSorter,
      flex: 1,
      width: 200,
    },
    {
      field: "inboundDeliveryTypeId",
      headerName: `${t("Inbound Delivery Type")}`,
      flex: 1,
    },
    {
      field: "scheduleDate",
      headerName: `${t("Schedule Date")}`,
      sortComparator: DateSorter,
      flex: 1,
      maxWidth: 110,
    },
    {
      field: "pocName",
      headerName: `${t("POC Name")}`,
      flex: 1,
      maxWidth: 100,
    },
    {
      field: "pocPhone",
      headerName: `${t("POC Phone")}`,
      flex: 1,
      // it has to be a number to be able to sort
      type: "number",
      headerAlign: "left",
      align: "left",
    },
    {
      field: "status",
      headerName: `${t("Status")}`,
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => {
        return (
          <div
            style={{
              backgroundColor:
                inboundRequestStatusColor.light[
                  inboundRequestStatuses.find(
                    (e: any) => e?.en_value === params.value,
                  )?.id
                ] || "white",
              color: `${
                inboundRequestStatusColor.regular[
                  inboundRequestStatuses.find(
                    (e: any) => e?.en_value === params.value,
                  )?.id
                ]
              }`,
              padding: "5px",
              margin: "5px",
              borderRadius: "4px",
              width: 90,
              textAlign: "center",
            }}
          >
            {params.value}
          </div>
        );
      },
    },
  ];

  const filterByDate = (from: Date, to: Date) => {
    if (from && to) {
      const sd = moment(from).format(API_DATE_FORMAT);
      const ed = moment(to).format(API_DATE_FORMAT);
      setStartDate(sd);
      setEndDate(ed);
    }
  };

  useEffect(() => {
    getData();
  }, [startDate, endDate, i18n.language]);

  const validateForm = () => {
    const newErrors: any = {};
    // startDate and endDate and customerId are required
    if (!startDate) {
      newErrors["startDate"] = t("Start Date is required");
    }
    if (!endDate) {
      newErrors["endDate"] = t("End Date is required");
    }
    if (startDate && endDate && moment(startDate).isAfter(moment(endDate))) {
      newErrors["endDate"] = t("End Date cannot be before Start Date");
    }
    if (!tenantIdFromUser) {
      newErrors["tenantIdFromUser"] = t("Tenant ID is required");
    }
    setError(
      Object.keys(newErrors).length > 0,
      newErrors["startDate"] ||
        newErrors["endDate"] ||
        newErrors["tenantIdFromUser"],
    );
    return Object.keys(newErrors).length === 0;
  };
  const getData = async () => {
    // Validate the form before making the API call
    if (!validateForm()) {
      return;
    }
    setIsLoading(true);
    const filters = {
      tenantId: tenantIdFromUser,
      startDate: startDate,
      endDate: endDate,
    };
    getInboundReportForTenant(filters)
      .then((response) => {
        const updatedRows = response.map((request: any) => ({
          ...request,
          requestDate: moment(request.requestDate).format(DATETIME_FORMAT),
          scheduleDate: moment(request.scheduleDate).format(DATE_FORMAT),
          inboundDeliveryTypeId: getInboundDeliveryTypeValue(
            request.inboundDeliveryTypeId,
          ),
          status: getInboundRequestStatusValue(request.status),
          statusNumber: request.status,
        }));
        // sort desc by date
        updatedRows.sort((a: any, b: any) =>
          moment(a.requestDate).isBefore(moment(b.requestDate)) ? 1 : -1,
        );
        setRows(updatedRows);
      })
      .catch((error) => {
        // Handle any errors that occur during the API call
        console.error("Error fetching inbound report:", error);
        setError(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <GridDX container columnSpacing={1} rowSpacing={2} sx={{ width: "100%" }}>
      <GridDX item xs={6} sx={{ marginBottom: 5 }}>
        <PageTitle />
      </GridDX>
      <GridDX
        item
        xs={6}
        sx={{
          marginBottom: 5,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <UnitsRangePicker
          toFilter={filterByDate}
          defaultFilter="currentMonth"
        />
      </GridDX>

      <GridDX item xs={12}>
        <ListPageDX
          getRowId={(row: any) => row.inboundRequestId}
          listTitle={t("Inbound Report")}
          name={t("SKU")}
          rows={Array.isArray(rows) ? rows : []}
          columns={columns}
          showAddButton={false}
          searchAndFilter={false}
          exportToPDF={false}
          exportToCSV={false}
          exportDropdownLabel={t("Download Report")}
          exportDropdownActionsLabels={[t("Export to PDF"), t("Export to CSV")]}
          isLoading={isLoading}
        />
      </GridDX>
    </GridDX>
  );
};

export default InboundReport;
